import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

interface PasswordChangeResponse {
  message: string;
  currentSessionActive: boolean;
}

@Component({
  selector: 'sham-reset-password-button',
  template: `
    <button type="button" class="reset-password-trigger" (click)="open()">Reset Password</button>
    <dialog #dialog class="reset-password-dialog" aria-labelledby="reset-password-title">
      <div class="reset-password-box">
        <h2 id="reset-password-title" class="reset-password-title">Reset Password</h2>
        <div class="reset-password-content">
          <label class="reset-password-field">
            <span>Current Password</span>
            <input type="password" autocomplete="current-password" [value]="currentPassword()" (input)="currentPassword.set($any($event.target).value)" />
          </label>
          <label class="reset-password-field">
            <span>New Password</span>
            <input type="password" autocomplete="new-password" [value]="newPassword()" (input)="newPassword.set($any($event.target).value)" />
          </label>
          <label class="reset-password-field">
            <span>Confirm New Password</span>
            <input type="password" autocomplete="new-password" [value]="confirmPassword()" (input)="confirmPassword.set($any($event.target).value)" />
          </label>
          @if (error()) { <p class="reset-password-error" role="alert">{{ error() }}</p> }
          <div class="reset-password-actions">
            <button type="button" class="reset-password-cancel" [disabled]="pending()" (click)="close()">Cancel</button>
            <button type="button" class="reset-password-confirm" [disabled]="pending()" (click)="submit()">{{ pending() ? 'Saving...' : 'Confirm' }}</button>
          </div>
        </div>
      </div>
      <form method="dialog" class="reset-password-backdrop"><button type="submit">Close</button></form>
    </dialog>
  `,
  styles: `
    .reset-password-trigger,
    .reset-password-cancel,
    .reset-password-confirm {
      height: 40px;
      border: 0;
      border-radius: 4px;
      padding: 0 2rem;
      font: inherit;
      font-weight: 600;
      cursor: pointer;
    }

    .reset-password-trigger,
    .reset-password-confirm {
      background: #2192BA;
      color: #ffffff;
    }

    .reset-password-dialog {
      position: fixed;
      top: 50%;
      left: 50%;
      width: min(36rem, calc(100vw - 2rem));
      max-height: calc(100vh - 2rem);
      margin: 0;
      transform: translate(-50%, -50%);
      overflow: auto;
      border: 0;
      border-radius: 0.5rem;
      padding: 0;
      color: #191c1f;
      background: #ffffff;
      box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
    }

    .reset-password-dialog::backdrop {
      background: rgb(0 0 0 / 0.3);
    }

    .reset-password-backdrop {
      display: none;
    }

    .reset-password-title {
      margin: 0;
      padding: 1.25rem 1.5rem;
      color: #2596BE;
      background: rgb(33 146 186 / 0.1);
      font-size: 24px;
      font-weight: 600;
    }

    .reset-password-content {
      display: grid;
      gap: 1.25rem;
      padding: 1.5rem;
    }

    .reset-password-field {
      display: grid;
      gap: 0.5rem;
      font-size: 0.875rem;
    }

    .reset-password-field input {
      box-sizing: border-box;
      width: 100%;
      min-height: 2.5rem;
      border: 1px solid #d1d5db;
      border-radius: 0.25rem;
      padding: 0 0.75rem;
      color: #191c1f;
      background: #ffffff;
      font: inherit;
    }

    .reset-password-field input:focus {
      outline: 2px solid #2192ba;
      outline-offset: 1px;
    }

    .reset-password-actions {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 1rem;
    }

    .reset-password-cancel {
      color: #191c1f;
      background: #ffffff;
      border: 1px solid #B6B6B6;
    }

    .reset-password-error {
      margin: 0;
      color: #b42318;
      font-size: 0.875rem;
    }

    .reset-password-trigger:hover,
    .reset-password-confirm:hover {
      background: #176b88;
    }

    button:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordButton {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly dialog = viewChild<ElementRef<HTMLDialogElement>>('dialog');

  readonly loginPath = input.required<string[]>();
  readonly endpoint = input.required<string>();
  readonly currentPassword = signal('');
  readonly newPassword = signal('');
  readonly confirmPassword = signal('');
  readonly error = signal('');
  readonly pending = signal(false);

  open() {
    this.currentPassword.set('');
    this.newPassword.set('');
    this.confirmPassword.set('');
    this.error.set('');
    this.dialog()?.nativeElement.showModal();
  }

  close() {
    if (!this.pending()) this.dialog()?.nativeElement.close();
  }

  async submit() {
    if (!this.currentPassword() || !this.newPassword() || !this.confirmPassword()) {
      this.error.set('All password fields are required.');
      return;
    }
    if (this.newPassword() !== this.confirmPassword()) {
      this.error.set('New password and confirmation do not match.');
      return;
    }

    this.pending.set(true);
    this.error.set('');
    try {
      const response = await firstValueFrom(this.http.post<PasswordChangeResponse>(this.endpoint(), {
        currentPassword: this.currentPassword(), newPassword: this.newPassword(), confirmPassword: this.confirmPassword(),
      }));
      this.dialog()?.nativeElement.close();
      if (!response.currentSessionActive) {
        sessionStorage.clear();
        localStorage.removeItem('refreshToken');
        await this.router.navigate(this.loginPath());
      }
    } catch {
      this.error.set('Unable to change password.');
    } finally {
      this.pending.set(false);
    }
  }
}
